<template>
  <loader v-if="isSaving" />
  <div v-else>
    <div v-if="isStockOrder" class="stock-order-notice" data-qa="stock-order-notice">
      <icon icon="info-plain" large />
      <div>
        <strong>{{ i18n.t('/StockOrder/CustomerNotice/Title') }}</strong>
        <p>{{ i18n.t('/StockOrder/CustomerNotice/Text') }}</p>
        <app-button
          v-if="isEditable"
          data-qa="cancel-stock-order-button"
          button-class="action-quiet"
          class="stock-order-notice__cancel"
          @click="cancelStockOrder"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
          <span>Deselecteer</span>
        </app-button>
      </div>
    </div>
    <div v-else-if="hasCustomer">
      <customer-details :customer="customer" />
      <div class="customer-actions-row">
        <edit-customer
          :customer="customer"
          :disabled="!isEditable"
          @update-customer="updateCurrentCustomer"
        />
        <app-button
          :disabled="!isEditable"
          data-qa="deselect-customer-button"
          button-class="action-quiet"
          @click="deselectCustomer"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
          <span>Deselecteer</span>
        </app-button>
      </div>
      <horizontal-line v-if="!hasCompany" />
    </div>
    <div v-else class="search-customer">
      <search-customer-modal
        :show="showModal"
        :is-purchase-order-customer-search="false"
        :show-common-drivers-checkbox="showCommonDriversCheckbox"
        :disable-add-customer="disableAddCustomer"
        :page-type="pageTypes.QUOTE"
        @create-customer="createAndAddCustomer"
        @select-customer="selectCustomer"
        @close="toggleModal(false)"
      />
      <app-button
        data-qa="customer-search-button"
        button-class="search-action"
        :disabled="!isEditable"
        @click="toggleModal(true)"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        <span>{{ i18n.t('/KlantZoekenModal/ZoekKlant') }}</span>
      </app-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import StoreConnector from '@/helpers/StoreConnector'
import { EventBus } from '@/plugins/event-bus'
import CustomersApi from '@/api/server/Customers'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import CustomerDetails from '@/components/generic/presentational/molecules/customerDetails/CustomerDetails.vue'
import EditCustomer from '@/components/generic/presentational/organisms/editCustomer/EditCustomer.vue'
import SearchCustomerModal from '@/components/generic/presentational/organisms/searchCustomerModal/SearchCustomerModal.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import HorizontalLine from '@/components/generic/presentational/atoms/horizontalLine/HorizontalLine.vue'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import { pageTypes } from '@/helpers/pageTypeConstants'

export default {
  components: {
    CustomerDetails,
    EditCustomer,
    AppButton,
    SearchCustomerModal,
    Loader,
    HorizontalLine,
    Icon,
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      customer: null,
      hasCustomer: false,
      hasCompany: false,
      isEditable: true,
      showModal: false,
      disableAddCustomer: false,
      isSaving: false,
      showCommonDriversCheckbox: false,
      isStockOrder: false,
    }
  },
  computed: {
    ...mapState('user', ['dealerId', 'dealerSettings']),
    pageTypes() {
      return pageTypes
    },
  },
  async created() {
    StoreConnector.get('quote/customer', (data) => {
      this.customer = data
    })

    StoreConnector.get('quote/hasCustomer', (data) => {
      this.hasCustomer = data
    })

    StoreConnector.get('quote/customerCompany', (data) => {
      this.hasCompany = data?.CustomerCompanyName != null
    })

    StoreConnector.get('quote/quoteStatusIsSalesOrder', (data) => {
      this.isEditable = !data
    })

    StoreConnector.get('quote/isStockOrder', (data) => {
      this.isStockOrder = data
    })

    this.showCommonDriversCheckbox =
      this.$route?.params.cstType === 'b2b'
        ? !!this.dealerSettings.commonDriversEnabledOnB2B
        : !!this.dealerSettings.commonDriversEnabledOnB2C
  },
  methods: {
    async toggleModal(showModal) {
      this.showModal = showModal

      if (showModal) {
        await this.searchRecommendedCustomers()
      }
    },
    async searchRecommendedCustomers() {
      const companyMagicNumber =
        window.VUE_CLI_EXPORTS.plugins.Store.getters['quote/customerCompany']
          ?.CustomerCompanyMagicNumber
      if (companyMagicNumber == 0 || companyMagicNumber == null) return
      await StoreConnector.dispatch('customer/searchRecommendedDealerCustomers', companyMagicNumber)
    },
    async createAndAddCustomer(customer) {
      try {
        this.isSaving = true
        const quoteId = this.$route.params.quoteId
        const newCustomer = await CustomersApi.createAccount(customer, quoteId)
        await StoreConnector.dispatch('quote/setCustomer', newCustomer)
        EventBus.$emit('quote/customerUpdated')
      } catch (e) {
        EventBus.$emit('notification-warning', e.data.Messages)
      } finally {
        this.isSaving = false
      }
    },
    async selectCustomer(selectedCustomer) {
      let isCompany = selectedCustomer?.AutolineCustomerType === 'C'
      if (!isCompany) {
        try {
          this.isSaving = true
          let addedCustomer = await CustomersApi.addCustomer(
            this.$route.params.quoteId,
            this.getPoIdFromCustomer(selectedCustomer),
            selectedCustomer.CustomerSource
          )
          await StoreConnector.dispatch('quote/setCustomer', addedCustomer)

          this.isSaving = false
          EventBus.$emit('quote/customerUpdated')
        } catch (e) {
          this.isSaving = false
          EventBus.$emit('notification-warning', e.data?.Messages || ['Failed to add customer'])
        }
      } else {
        const namespace = 'quote'

        StoreConnector.dispatch(`${namespace}/setCustomerCompany`, selectedCustomer).then(() => {
          EventBus.$emit(`${namespace}/companyUpdated`)
        })
      }
    },
    getPoIdFromCustomer(customer) {
      if (customer.CustomerMagicNumber) {
        return customer.CustomerMagicNumber
      } else if (customer.PoId) {
        return customer.PoId
      }
      throw new Error("Can't determine PoId for the selected customer")
    },
    updateCurrentCustomer(customer) {
      CustomersApi.updateAccount(customer, this.dealerId, this.$route.params.quoteId)
        .then((response) => {
          StoreConnector.dispatch('quote/setCustomer', customer).then(() => {
            EventBus.$emit('quote/customerUpdated')
          })
        })
        .catch((response) => {})
    },
    async deselectCustomer() {
      try {
        this.isSaving = true
        // Use the new API to remove customer from quote
        await CustomersApi.removeCustomer(this.$route.params.quoteId)

        // Update the store to remove customer data
        await StoreConnector.dispatch('quote/setCustomer', {})

        this.isSaving = false
        EventBus.$emit('quote/customerRemoved')
      } catch (e) {
        this.isSaving = false
        EventBus.$emit('notification-warning', e.data?.Messages || ['Failed to remove customer'])
      }
    },
    async cancelStockOrder() {
      try {
        this.isSaving = true
        await StoreConnector.dispatch('quote/setStockOrder', false)
        EventBus.$emit('quote-is-stock-changed')
      } catch (e) {
        EventBus.$emit('notification-warning', e.data?.Messages || ['Failed to cancel stock order'])
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.customer-actions-row {
  display: flex;
  gap: 8px;
}

.stock-order-notice {
  display: flex;
  gap: 0.75em;
  align-items: flex-start;
  padding: 1em 1.1em;
  border: 1px solid $gray-quiet-border;
  border-radius: $border-radius-lg;
  background: $gray-lightest;

  strong {
    display: block;
    margin-bottom: 0.25em;
  }

  p {
    margin: 0;
    color: $gray-quiet-text;
  }
}

.stock-order-notice__cancel {
  margin-top: 0.75em;
}
</style>
